import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Users,
  Crown,
  Shield,
  UserPlus,
  Trash2,
  Loader2,
  Settings,
  LogOut,
  Mail,
} from "lucide-react";
import { useOrganization, OrganizationMember } from "@/hooks/useOrganization";
import { PendingInvitationsList } from "@/components/organization/PendingInvitationsList";
import { cn } from "@/lib/utils";

export default function OrganizationSettings() {
  const {
    organization,
    members,
    userRole,
    isAdmin,
    isOwner,
    loading,
    membersLoading,
    createOrganization,
    updateOrganization,
    inviteMember,
    updateMemberRole,
    removeMember,
    leaveOrganization,
  } = useOrganization();

  const [orgName, setOrgName] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [isCreating, setIsCreating] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editName, setEditName] = useState("");

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim() || !orgSlug.trim()) return;

    setIsCreating(true);
    await createOrganization({
      name: orgName.trim(),
      slug: orgSlug.trim(),
    });
    setIsCreating(false);
    setOrgName("");
    setOrgSlug("");
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    const success = await inviteMember(inviteEmail.trim(), inviteRole);
    if (success) {
      setInviteEmail("");
    }
    setIsInviting(false);
  };

  const handleUpdateName = async () => {
    if (!editName.trim() || editName === organization?.name) return;

    setIsUpdating(true);
    await updateOrganization({ name: editName.trim() });
    setIsUpdating(false);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "owner":
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case "admin":
        return <Shield className="w-4 h-4 text-primary" />;
      default:
        return <Users className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "owner":
        return "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400";
      case "admin":
        return "bg-primary/20 text-primary";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // No organization yet - show create form
  if (!organization) {
    return (
      <div className="min-h-screen p-6 md:p-8 lg:p-10">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-display font-bold mb-1">Organization</h1>
          <p className="text-muted-foreground">Create an organization to collaborate with your team</p>
        </div>

        <Card className="glass max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Create Organization
            </CardTitle>
            <CardDescription>
              Set up a workspace for your team to share projects and credits
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateOrg} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name</Label>
                <Input
                  id="org-name"
                  placeholder="Acme Inc."
                  value={orgName}
                  onChange={(e) => {
                    setOrgName(e.target.value);
                    // Auto-generate slug
                    setOrgSlug(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9\s-]/g, "")
                        .replace(/\s+/g, "-")
                        .slice(0, 30)
                    );
                  }}
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="org-slug">URL Slug</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">reqqy.app/org/</span>
                  <Input
                    id="org-slug"
                    placeholder="acme-inc"
                    value={orgSlug}
                    onChange={(e) =>
                      setOrgSlug(
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, "")
                          .slice(0, 30)
                      )
                    }
                    className="h-10 flex-1"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isCreating || !orgName.trim() || !orgSlug.trim()}
                className="w-full gradient-primary text-primary-foreground"
              >
                {isCreating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Create Organization
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8 lg:p-10">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-display font-bold mb-1">Organization</h1>
        <p className="text-muted-foreground">Manage your organization settings and team members</p>
      </div>

      <div className="max-w-3xl space-y-6">
        {/* Organization Details */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  {organization.name}
                </CardTitle>
                <CardDescription>/{organization.slug}</CardDescription>
              </div>
              <Badge className={getRoleBadgeColor(userRole || "member")}>
                {getRoleIcon(userRole || "member")}
                <span className="ml-1 capitalize">{userRole}</span>
              </Badge>
            </div>
          </CardHeader>
          {isAdmin && (
            <CardContent className="space-y-4">
              <div className="flex items-end gap-4">
                <div className="flex-1 space-y-2">
                  <Label>Organization Name</Label>
                  <Input
                    defaultValue={organization.name}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-12"
                  />
                </div>
                <Button
                  onClick={handleUpdateName}
                  disabled={isUpdating}
                  variant="outline"
                >
                  {isUpdating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Settings className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Team Members */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Team Members
            </CardTitle>
            <CardDescription>
              {members.length} member{members.length !== 1 ? "s" : ""} in your organization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Invite Form */}
            {isAdmin && (
              <form onSubmit={handleInvite} className="flex items-end gap-3 p-4 rounded-xl bg-background/50">
                <div className="flex-1 space-y-2">
                  <Label>Invite by email</Label>
                  <Input
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as "admin" | "member")}>
                    <SelectTrigger className="w-32 h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  disabled={isInviting || !inviteEmail.trim()}
                  className="gradient-primary text-primary-foreground h-12"
                >
                  {isInviting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Invite
                    </>
                  )}
                </Button>
            </form>
            )}

            {/* Pending Invitations */}
            {isAdmin && <PendingInvitationsList />}

            {/* Members List */}
            <div className="space-y-2">
              {membersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                members.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    isAdmin={isAdmin}
                    isOwner={isOwner}
                    onUpdateRole={updateMemberRole}
                    onRemove={removeMember}
                  />
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Leave Organization */}
        {!isOwner && (
          <Card className="glass border-destructive/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium">Leave Organization</p>
                <p className="text-sm text-muted-foreground">
                  Remove yourself from {organization.name}
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-destructive hover:text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Leave
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Leave organization?</AlertDialogTitle>
                    <AlertDialogDescription>
                      You will lose access to all shared projects and resources. An admin will need to invite you again if you want to rejoin.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={leaveOrganization}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Leave Organization
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

interface MemberRowProps {
  member: OrganizationMember;
  isAdmin: boolean;
  isOwner: boolean;
  onUpdateRole: (memberId: string, role: "admin" | "member") => Promise<boolean>;
  onRemove: (memberId: string) => Promise<boolean>;
}

function MemberRow({ member, isAdmin, isOwner, onUpdateRole, onRemove }: MemberRowProps) {
  const [updating, setUpdating] = useState(false);

  const handleRoleChange = async (role: "admin" | "member") => {
    setUpdating(true);
    await onUpdateRole(member.id, role);
    setUpdating(false);
  };

  const displayName = member.profile?.full_name || member.profile?.email || "Unknown User";
  const email = member.profile?.email || "";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const canManage = isAdmin && member.role !== "owner";
  const canChangeRole = isOwner && member.role !== "owner"; // Only owner can change roles

  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-background/50 hover:bg-background/80 transition-colors">
      <Avatar className="h-10 w-10">
        <AvatarImage src={member.profile?.avatar_url || undefined} alt={displayName} />
        <AvatarFallback className="bg-primary/10 text-primary">{initials}</AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{displayName}</p>
        <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
          <Mail className="w-3 h-3" />
          {email}
        </p>
      </div>

      <div className="flex items-center gap-2">
        {canChangeRole ? (
          <Select
            value={member.role}
            onValueChange={(v) => handleRoleChange(v as "admin" | "member")}
            disabled={updating}
          >
            <SelectTrigger className="w-28 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Badge className={cn("capitalize", getRoleBadgeColor(member.role))}>
            {member.role}
          </Badge>
        )}

        {canManage && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove member?</AlertDialogTitle>
                <AlertDialogDescription>
                  {displayName} will lose access to the organization and all shared resources.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onRemove(member.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

function getRoleBadgeColor(role: string) {
  switch (role) {
    case "owner":
      return "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400";
    case "admin":
      return "bg-primary/20 text-primary";
    default:
      return "bg-muted text-muted-foreground";
  }
}
